import { useEffect, useState } from "react";
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Pagination, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";
import { PiDotsThreeOutlineFill, PiEnvelopeSimpleLight } from "react-icons/pi";
import { TbFileExport } from "react-icons/tb";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CodeIcon from "@mui/icons-material/Code";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EmailIcon from "@mui/icons-material/Email";
import CancelIcon from "@mui/icons-material/Cancel";
import { ApiRequest } from "GlobalFunctions";
import { ProjectConfig } from "Global";
import { formatCurrency } from "./helpers/invoiceUtils";
import CreditNoteDialog from "./components/CreditNoteDialog";
import Filters2 from "elements/Filters/Filters2";
import FiltersApplied2 from "elements/Filters/FiltersApplied2";

const statusColors: any = { pending: "warning", sent: "info", accepted: "success", rejected: "error", error: "error" };
const statusLabels: any = { pending: "Pendiente", sent: "Enviado", accepted: "Aceptada", rejected: "Rechazada", error: "Error" };

export default function InvoiceList(props: any) {
  const { setLoading } = props;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [list, setList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);

  const [filtersValues, setFiltersValues] = useState<any>({});
  const [openFilters, setOpenFilters] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);

  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailDocId, setEmailDocId] = useState<number | null>(null);

  const [openVoidDialog, setOpenVoidDialog] = useState(false);
  const [voidingDoc, setVoidingDoc] = useState<any>(null);

  useEffect(() => { setLoading(false); }, []);
  useEffect(() => { loadList(); }, [page, filtersValues]);

  const filters = [
    { type: "text", label: "Cliente (nombre o negocio)", name: "search", value: "" },
    { type: "date_range", label: "Fecha", name: "date", value: "" },
  ];

  const setFilters = (tempFilters: any) => {
    setFiltersValues(tempFilters);
  };

  const loadList = () => {
    setLoading(true);
    const query: any = { page, document_type: "invoice" };

    if (filtersValues.search?.value) query.search = filtersValues.search.value;
    if (filtersValues.date?.value) {
      query.start_date = filtersValues.date.value.startDate;
      query.end_date = filtersValues.date.value.endDate;
    }

    ApiRequest({
      url: "/dian/documents/list",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query,
      setResponse: (response: any) => {
        setList(response.list || []);
        setTotalPages(response.total_pages || 1);
        setLoading(false);
      },
    });
  };

  const exportExcel = (docType: string) => {
    if (!filtersValues.search?.value && !filtersValues.date?.value) {
      enqueueSnackbar("Debe aplicar al menos un filtro antes de exportar", { variant: "warning" });
      return;
    }
    setLoading(true);
    const query: any = { document_type: docType };
    if (filtersValues.search?.value) query.search = filtersValues.search.value;
    if (filtersValues.date?.value) {
      query.start_date = filtersValues.date.value.startDate;
      query.end_date = filtersValues.date.value.endDate;
    }

    ApiRequest({
      url: "/dian/documents/export-excel",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query,
      setResponse: (response: any) => {
        if (response.status === "success" && response.file) {
          const link = document.createElement("a");
          link.href = response.file;
          link.download = "";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          enqueueSnackbar(response.error || "Error al exportar", { variant: "error" });
        }
        setLoading(false);
      },
    });
  };

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>, doc: any) => {
    setAnchorEl(e.currentTarget);
    setSelectedDoc(doc);
  };

  const handleMenuClose = () => { setAnchorEl(null); setSelectedDoc(null); };

  const downloadPdf = (doc: any) => {
    if (doc.pdf_url) window.open(`${ProjectConfig.api_url}${doc.pdf_url}`, "_blank");
  };

  const downloadXml = (docId: number) => {
    const ltkn = localStorage.getItem("ltkn");
    window.open(`${ProjectConfig.api_url}/dian/document/xml?document_id=${docId}&ltkn=${ltkn}`, "_blank");
  };

  const checkStatus = (docId: number) => {
    ApiRequest({
      url: "/dian/document/status",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      query: { document_id: docId },
      setResponse: (response: any) => {
        enqueueSnackbar(`Estado: ${statusLabels[response.status] || response.status}`, { variant: "info" });
        loadList();
      },
    });
  };

  const retryDocument = (docId: number) => {
    ApiRequest({
      url: "/dian/document/retry",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: { document_id: docId },
      setResponse: (response: any) => {
        if (response.success) {
          enqueueSnackbar("Documento reenviado exitosamente", { variant: "success" });
        } else {
          const dianErrors = response.dianResponse?.errors;
          const reason = Array.isArray(dianErrors) && dianErrors.length
            ? dianErrors.join(' | ')
            : (response.error || 'Error al reenviar documento');
          enqueueSnackbar(reason, { variant: 'error', autoHideDuration: 12000, style: { whiteSpace: 'pre-line' } });
        }
        loadList();
      },
    });
  };

  const handleOpenEmailDialog = (doc: any) => {
    setEmailDocId(doc.document_id);
    setEmailList([]);
    setEmailInput('');
    setOpenEmailDialog(true);
  };

  const handleAddEmail = () => {
    const emails = emailInput.split(',').map(e => e.trim()).filter(e => e && /\S+@\S+\.\S+/.test(e));
    if (emails.length) {
      setEmailList(prev => [...prev, ...emails.filter(e => !prev.includes(e))]);
      setEmailInput('');
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); handleAddEmail(); }
  };

  const handleSendEmail = () => {
    if (!emailList.length || !emailDocId) return;
    setSendingEmail(true);
    ApiRequest({
      url: '/dian/document/sendEmail',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: { document_id: emailDocId, emails: emailList },
      setResponse: (response: any) => {
        setSendingEmail(false);
        if (response.status === 'success') {
          enqueueSnackbar('Factura enviada por correo', { variant: 'success' });
          setOpenEmailDialog(false);
        } else {
          enqueueSnackbar(response.message || 'Error al enviar', { variant: 'error' });
        }
      },
    });
  };

  const handleVoidSuccess = () => {
    enqueueSnackbar('Nota crédito emitida correctamente', { variant: 'success' });
    loadList();
  };

  return (
    <div id="InvoiceList" className="ModuleContainer">
      <div className="ModuleWrapper">

        {/* HEADER */}
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Facturas Electronicas</h2>
            <span className="h2_subtitle">Facturas emitidas ante la DIAN</span>
          </div>
          <div className="boxContainerActions">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<TuneIcon />}
              onClick={() => setOpenFilters(true)}
            >
              Filtrar
            </Button>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<TbFileExport />}
              onClick={() => exportExcel("invoice")}
            >
              Exportar Facturas
            </Button>

            <Button
              variant="contained"
              color="secondary"
              startIcon={<TbFileExport />}
              onClick={() => exportExcel("credit_note")}
            >
              Exportar NC
            </Button>

            <Button
              variant="contained"
              onClick={() => navigate("/invoicing/facturas/crear")}
              startIcon={<AddIcon />}
              sx={{ borderRadius: 2, textTransform: "none", px: 3 }}
            >
              Nueva Factura
            </Button>
          </div>
        </div>

        <Filters2 open={openFilters} setOpen={setOpenFilters} filters={filters} setFilters={setFilters} />
        <FiltersApplied2 filtersValues={filtersValues} setFiltersValues={setFiltersValues} filters={filters} />

        {/* TABLE */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    Numero
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    Cliente
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    NIT
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    Total
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "text.secondary", fontSize: 13, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    Fecha
                  </TableCell>
                  <TableCell sx={{ py: 2, borderBottom: "1px solid", borderColor: "divider", width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((doc: any) => (
                  <TableRow
                    key={doc.document_id}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:last-child td": { borderBottom: 0 },
                      transition: "background-color 0.15s",
                    }}
                    onClick={() => navigate(`/invoicing/facturas/ver/${doc.document_id}`)}
                  >
                    <TableCell sx={{ py: 2, fontWeight: 600, color: "text.primary" }}>
                      {doc.prefix}{doc.number}
                    </TableCell>
                    <TableCell sx={{ py: 2, color: "text.primary" }}>
                      {doc.buyer_name}
                    </TableCell>
                    <TableCell sx={{ py: 2, color: "text.secondary", fontSize: 13 }}>
                      {doc.buyer_nit}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2, fontWeight: 600, fontFamily: "monospace", color: "text.primary" }}>
                      {formatCurrency(doc.payable_amount || 0)}
                    </TableCell>
                    <TableCell sx={{ py: 2 }} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Chip
                          label={statusLabels[doc.status] || doc.status}
                          color={statusColors[doc.status] || "default"}
                          size="small"
                          sx={{ fontWeight: 500, fontSize: 12, borderRadius: 1.5 }}
                        />
                        {doc.email_status && (
                          <Tooltip
                            arrow
                            placement="top"
                            title={doc.email_status === 'sent' ? 'Correo enviado' : 'Error al enviar correo'}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <PiEnvelopeSimpleLight
                                style={{
                                  fontSize: '18px',
                                  color: doc.email_status === 'sent' ? '#4caf50' : '#f44336',
                                }}
                              />
                            </span>
                          </Tooltip>
                        )}
                        {Number(doc.credited_amount) > 0 && (
                          <Chip
                            label={
                              Number(doc.credited_amount) >= Number(doc.payable_amount)
                                ? "Anulada"
                                : `${doc.credit_note_count > 1 ? doc.credit_note_count + ' NC · ' : 'NC '}${formatCurrency(doc.credited_amount)}`
                            }
                            color={Number(doc.credited_amount) >= Number(doc.payable_amount) ? "error" : "warning"}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 0.5, fontWeight: 500, fontSize: 12, borderRadius: 1.5 }}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell sx={{ py: 2, color: "text.secondary", fontSize: 13 }}>
                      {doc.issue_date ? new Date(doc.issue_date).toLocaleDateString("es-CO") : ""}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2 }} onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={(e) => handleMenuClick(e, doc)} sx={{ color: "text.secondary" }}>
                        <PiDotsThreeOutlineFill />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {list.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay facturas electronicas
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", padding: "16px 0", borderTop: "1px solid #eee" }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" shape="rounded" />
            </div>
          )}
        </Paper>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 2,
            sx: { borderRadius: 2, minWidth: 200, py: 0.5 },
          }}
        >
          <MenuItem onClick={() => { navigate(`/invoicing/facturas/ver/${selectedDoc?.document_id}`); handleMenuClose(); }} sx={{ py: 1.2, gap: 1.5 }}>
            <VisibilityIcon fontSize="small" color="action" /> Ver Factura
          </MenuItem>
          {selectedDoc?.status === "accepted" && selectedDoc?.pdf_path && (
            <MenuItem onClick={() => { downloadPdf(selectedDoc); handleMenuClose(); }} sx={{ py: 1.2, gap: 1.5 }}>
              <PictureAsPdfIcon fontSize="small" color="action" /> Descargar PDF
            </MenuItem>
          )}
          {selectedDoc?.status === "accepted" && (
            <MenuItem onClick={() => { downloadXml(selectedDoc?.document_id); handleMenuClose(); }} sx={{ py: 1.2, gap: 1.5 }}>
              <CodeIcon fontSize="small" color="action" /> Descargar XML
            </MenuItem>
          )}
          <MenuItem onClick={() => { checkStatus(selectedDoc?.document_id); handleMenuClose(); }} sx={{ py: 1.2, gap: 1.5 }}>
            <RefreshIcon fontSize="small" color="action" /> Consultar Estado
          </MenuItem>
          {(selectedDoc?.status === "error" || selectedDoc?.status === "rejected") && (
            <MenuItem onClick={() => { retryDocument(selectedDoc.document_id); handleMenuClose(); }} sx={{ py: 1.2, gap: 1.5 }}>
              <ReplayIcon fontSize="small" color="action" /> Reintentar Envio
            </MenuItem>
          )}
          {selectedDoc?.status === "accepted" && (
            <MenuItem onClick={() => { handleOpenEmailDialog(selectedDoc); handleMenuClose(); }} sx={{ py: 1.2, gap: 1.5 }}>
              <EmailIcon fontSize="small" color="action" /> Enviar por Correo
            </MenuItem>
          )}
          {selectedDoc?.status === "accepted" && !(Number(selectedDoc?.credited_amount) >= Number(selectedDoc?.payable_amount)) && (
            <MenuItem onClick={() => { setVoidingDoc(selectedDoc); setOpenVoidDialog(true); handleMenuClose(); }} sx={{ py: 1.2, gap: 1.5, color: "error.main" }}>
              <CancelIcon fontSize="small" /> Nota Crédito
            </MenuItem>
          )}
        </Menu>

        {/* Send Email Dialog */}
        <Dialog open={openEmailDialog} onClose={() => setOpenEmailDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>Enviar factura por correo</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16, marginTop: 8 }}>
              {emailList.map((email, i) => (
                <Chip key={i} label={email} onDelete={() => setEmailList(prev => prev.filter((_, idx) => idx !== i))} size="small" sx={{ borderRadius: 1.5 }} />
              ))}
            </div>
            <TextField
              fullWidth size="small"
              label="Agregar correos (separados por coma o Enter)"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleEmailKeyDown}
              onBlur={handleAddEmail}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setOpenEmailDialog(false)} sx={{ borderRadius: 2, textTransform: "none" }}>Cancelar</Button>
            <Button variant="contained" onClick={handleSendEmail} disabled={!emailList.length || sendingEmail} sx={{ borderRadius: 2, textTransform: "none" }}>
              {sendingEmail ? <CircularProgress size={20} /> : 'Enviar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Credit Note Dialog */}
        <CreditNoteDialog
          open={openVoidDialog}
          onClose={() => setOpenVoidDialog(false)}
          doc={voidingDoc}
          onSuccess={handleVoidSuccess}
        />
      </div>
    </div>
  );
}
